import React, { FC, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Input, InputTypes, InputVariants } from '@components/atoms/Input';
import { Select, SelectType } from '@components/atoms/Select';
import { Space } from '@components/atoms/Space';
import { useNotifications } from '@hooks/useNotifications';
import { useData } from '@hooks/useData';
import { Form } from '@components/atoms/Form';
import { Typography } from '@components/atoms/Typography';
import { MyAccountContainer } from '@components/organisms/MyAccountContainer';
import { Accordion } from '@components/atoms/Accordion';
import { Table } from '@components/atoms/Table';
import { formatPrice } from '@utils/format';
import { useGetMyAdherenceUpdateStatus } from '@services/useGetMyAdherenceUpdateStatus';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { SITE_URLS } from '@constants';
import { Checkbox, CheckboxType } from '@components/atoms/Checkbox';
import { PopupConfirm } from '@components/molecules/PopupConfirm';
interface StoreData {
    uuid?: string;
    name?: string;
    logo?: {
        src?: string;
        type?: string;
        width?: number;
        height?: number;
    };
}
interface CategoryData {
    uuid?: string;
    name?: string;
    description?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    children?: any[];
}

interface ProductData {
    uuid?: string;
    name?: string;
    price?: number;
}

export const MyAdherenceDetail: FC = () => {
    const { t } = useTranslation();
    const { myAdherenceDetail } = useData();
    const formRef = useRef<HTMLFormElement>(null);
    const { showDefaultNotification } = useNotifications();
    const { navigate } = useCustomNavigate();

    const {
        request: requestUpdateStatus,
        success: updateSuccess,
        lastResponseTime: lastUpdate,
        errorMessage: updateErrorMessage,
    } = useGetMyAdherenceUpdateStatus();

    const [adherenceStatus, setAdherenceStatus] = useState<string>('');
    const [promotionTitle, setPromotionTitle] = useState<string>('');
    const [promotionDescription, setPromotionDescription] = useState<string>('');
    const [promotionDiscount, setPromotionDiscount] = useState<string>('');
    const [promotionMinimumOrderValue, setPromotionMinimumOrderValue] = useState<number | null>(null);
    const [promotionMaximumDiscountAmount, setPromotionMaximumDiscountAmount] = useState<number | null>(null);
    const [promotionUsageLimitPerCustomer, setPromotionUsageLimitPerCustomer] = useState<number | null>(null);
    const [promotionTotalUsageLimit, setPromotionTotalUsageLimit] = useState<number | null>(null);
    const [promotionStartDate, setPromotionStartDate] = useState<string>('');
    const [promotionEndDate, setPromotionEndDate] = useState<string>('');
    const [store, setStore] = useState<StoreData | null>(null);
    const [categories, setCategories] = useState<CategoryData[]>([]);
    const [products, setProducts] = useState<ProductData[]>([]);
    const [selectedProductUuids, setSelectedProductUuids] = useState<string[]>([]);
    const [showConfirmPopup, setShowConfirmPopup] = useState(false);

    // Translations for the form
    const translations = {
        titleLabel: t('adherence-detail-form.promotion.title'),
        descriptionLabel: t('adherence-detail-form.promotion.description'),
        startDateLabel: t('adherence-detail-form.promotion.start-date'),
        endDateLabel: t('adherence-detail-form.promotion.end-date'),
        minimumOrderValueLabel: t('adherence-detail-form.promotion.minimum_order_value'),
        maximumDiscountAmountLabel: t('adherence-detail-form.promotion.maximum_discount_amount'),
        usageLimitPerCustomerLabel: t('adherence-detail-form.promotion.usage_limit_per_customer'),
        totalUsageLimitLabel: t('adherence-detail-form.promotion.total_usage_limit'),
        discountLabel: t('adherence-detail-form.promotion.discount'),
        statusLabel: t('adherence-detail-form.status.label'),
        saveButton: t('adherence-detail-form.button.save'),
        statusAccepted: t('adherence-detail-form.status.accepted'),
        statusPending: t('adherence-detail-form.status.pending'),
        statusRejected: t('adherence-detail-form.status.rejected'),
        categoriesLabel: t('adherence-detail-form.categories.label'),
        categoryName: t('adherence-detail-form.categories.name'),
        categoryDescription: t('adherence-detail-form.categories.description'),
        storesLabel: t('adherence-detail-form.stores.label'),
        storeName: t('adherence-detail-form.stores.name'),
        productsLabel: t('adherence-detail-form.products.label'),
        productName: t('adherence-detail-form.products.name'),
        productPrice: t('adherence-detail-form.products.price'),
        confirmTitle: t('adherence-detail-form.pop-up.title'),
        confirm: t('adherence-detail-form.pop-up.confirm'),
        confirmButton: t('adherence-detail-form.pop-up.confirm-button'),
        cancelButton: t('adherence-detail-form.pop-up.cancel-button'),
    };

    useEffect(() => {
        if (myAdherenceDetail?.data) {
            setAdherenceStatus(myAdherenceDetail.data.adherence_status ?? '');
            setPromotionTitle(myAdherenceDetail.data.promotion?.title ?? '');
            setPromotionDescription(myAdherenceDetail.data.promotion?.description ?? '');
            setPromotionDiscount(myAdherenceDetail.data.promotion?.discount ?? '');
            setPromotionEndDate(myAdherenceDetail.data.promotion?.end_date ?? '');

            setPromotionMinimumOrderValue(myAdherenceDetail.data.promotion?.minimum_order_value ?? null);
            setPromotionMaximumDiscountAmount(myAdherenceDetail.data.promotion?.maximum_discount_amount ?? null);
            setPromotionUsageLimitPerCustomer(myAdherenceDetail.data.promotion?.usage_limit_per_customer ?? null);
            setPromotionTotalUsageLimit(myAdherenceDetail.data.promotion?.total_usage_limit ?? null);

            setPromotionStartDate(myAdherenceDetail.data.promotion?.start_date ?? '');
            setStore(myAdherenceDetail.data.store ?? null);
            setCategories(myAdherenceDetail.data.categories ?? []);
            const productsData = myAdherenceDetail.data.products ?? [];
            setProducts(productsData);

            setSelectedProductUuids(
                productsData.filter((product) => product.uuid !== undefined).map((product) => product.uuid as string)
            );
        }
    }, [myAdherenceDetail]);

    const handleProductSelection = (uuid: string) => {
        setSelectedProductUuids((prevUuids) => {
            if (prevUuids.includes(uuid)) {
                return prevUuids.filter((productUuid) => productUuid !== uuid);
            } else {
                return [...prevUuids, uuid];
            }
        });
    };

    const handleSubmit = () => {
        if (myAdherenceDetail?.data?.adherence_uuid && adherenceStatus) {
            requestUpdateStatus(
                { uuid: myAdherenceDetail.data.adherence_uuid },
                {
                    status: adherenceStatus,
                    accepted_product_uuids: selectedProductUuids,
                    store_uuid: store?.uuid,
                }
            );
        }
    };

    const handleConfirmSubmit = () => {
        setShowConfirmPopup(false);
        formRef?.current?.requestSubmit();
    };

    useEffect(() => {
        if (lastUpdate) {
            if (updateSuccess) {
                showDefaultNotification({ type: 'success', message: updateSuccess });
                navigate(`/${SITE_URLS.myAdherences}`);
            } else if (updateErrorMessage) {
                showDefaultNotification({ type: 'error', message: updateErrorMessage });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastUpdate]);

    return (
        <MyAccountContainer title={translations.titleLabel}>
            <div className="pt-4">
                <Form formRef={formRef} onSubmit={handleSubmit}>
                    <div className="row">
                        <div className="col-12">
                            <Input
                                type={InputTypes.text}
                                variant={InputVariants.labeledInput}
                                label={translations.titleLabel}
                                value={promotionTitle || ''}
                                disabled={true}
                                onChange={() => {}}
                            />
                            <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                        </div>
                        <div className="col-12">
                            <Input
                                type={InputTypes.text}
                                variant={InputVariants.labeledInput}
                                label={translations.descriptionLabel}
                                value={promotionDescription || ''}
                                disabled={true}
                                onChange={() => {}}
                            />
                            <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                        </div>
                        <div className="col-md-6 col-12">
                            <Input
                                type={InputTypes.text}
                                variant={InputVariants.labeledInput}
                                label={translations.startDateLabel}
                                value={promotionStartDate || ''}
                                disabled={true}
                                onChange={() => {}}
                            />
                            <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                        </div>
                        <div className="col-md-6 col-12">
                            <Input
                                type={InputTypes.text}
                                variant={InputVariants.labeledInput}
                                label={translations.endDateLabel}
                                value={promotionEndDate || ''}
                                disabled={true}
                                onChange={() => {}}
                            />
                            <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                        </div>
                        <div className="col-md-6 col-12">
                            <Input
                                type={InputTypes.text}
                                variant={InputVariants.labeledInput}
                                label={translations.discountLabel}
                                value={promotionDiscount || ''}
                                disabled={true}
                                onChange={() => {}}
                            />
                            <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                        </div>
                        <div className="col-md-6 col-12">
                            <Input
                                type={InputTypes.text}
                                variant={InputVariants.labeledInput}
                                label={translations.minimumOrderValueLabel}
                                value={formatPrice(promotionMinimumOrderValue) || ''}
                                disabled={true}
                                onChange={() => {}}
                            />
                            <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                        </div>
                        <div className="col-md-6 col-12">
                            <Input
                                type={InputTypes.text}
                                variant={InputVariants.labeledInput}
                                label={translations.maximumDiscountAmountLabel}
                                value={formatPrice(promotionMaximumDiscountAmount) || ''}
                                disabled={true}
                                onChange={() => {}}
                            />
                            <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                        </div>
                        <div className="col-md-6 col-12">
                            <Input
                                type={InputTypes.text}
                                variant={InputVariants.labeledInput}
                                label={translations.usageLimitPerCustomerLabel}
                                value={promotionUsageLimitPerCustomer ? promotionUsageLimitPerCustomer.toString() : ''}
                                disabled={true}
                                onChange={() => {}}
                            />
                            <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                        </div>
                        <div className="col-md-6 col-12">
                            <Input
                                type={InputTypes.text}
                                variant={InputVariants.labeledInput}
                                label={translations.totalUsageLimitLabel}
                                value={promotionTotalUsageLimit ? promotionTotalUsageLimit.toString() : ''}
                                disabled={true}
                                onChange={() => {}}
                            />
                            <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                        </div>
                    </div>

                    <hr />

                    <div className="row">
                        <div className="col-12">
                            <Typography>{translations.storesLabel}</Typography>
                            <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                            {store && (
                                <>
                                    <Input
                                        type={InputTypes.text}
                                        variant={InputVariants.labeledInput}
                                        label={translations.storeName}
                                        value={store.name || ''}
                                        disabled={true}
                                        onChange={() => {}}
                                    />
                                </>
                            )}
                        </div>
                    </div>

                    <hr />

                    <Accordion
                        items={[
                            {
                                id: 'categories-accordion',
                                buttonContent: <Typography>{translations.categoriesLabel}</Typography>,
                                bodyContent: (
                                    <Table
                                        headerCols={[
                                            { text: translations.categoryName, classes: 'text-left' },
                                            { text: translations.categoryDescription, classes: 'text-left' },
                                        ]}
                                        contentRows={(categories || []).map((category) => {
                                            return [
                                                { content: <Typography>{category.name}</Typography> },
                                                { content: <Typography>{category.description}</Typography> },
                                            ];
                                        })}
                                    />
                                ),
                            },

                            {
                                id: 'products-accordion',
                                buttonContent: <Typography>{translations.productsLabel}</Typography>,
                                bodyContent: (
                                    <Table
                                        headerCols={[
                                            { text: '', classes: 'text-left' },
                                            { text: translations.productName, classes: 'text-left' },
                                            { text: translations.productPrice, classes: 'text-right' },
                                        ]}
                                        contentRows={(products || []).map((product) => {
                                            const productUuid = product.uuid ?? '';

                                            return [
                                                {
                                                    content: (
                                                        <Checkbox
                                                            id={`product-checkbox-${productUuid}`}
                                                            name={`product-checkbox-${productUuid}`}
                                                            label={''}
                                                            value={productUuid}
                                                            type={CheckboxType.normal}
                                                            checked={selectedProductUuids.includes(productUuid)}
                                                            onChange={() => handleProductSelection(productUuid)}
                                                        />
                                                    ),
                                                },
                                                { content: <Typography>{product.name}</Typography> },
                                                { content: <Typography>{formatPrice(product.price)}</Typography> },
                                            ];
                                        })}
                                    />
                                ),
                            },
                        ]}
                    />

                    <hr />

                    <div className="row">
                        <div className="col-md-6 col-12">
                            <div>
                                <Typography color="muted" fontSize="12">
                                    <>{translations.statusLabel}</>
                                </Typography>
                                <Select
                                    classes="w-100"
                                    type={SelectType.variant1}
                                    options={[
                                        { value: 'pending', text: translations.statusPending },
                                        { value: 'accepted', text: translations.statusAccepted },
                                        { value: 'rejected', text: translations.statusRejected },
                                    ]}
                                    value={adherenceStatus}
                                    onChange={(value) => setAdherenceStatus(value)}
                                />
                            </div>
                            <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                        </div>
                    </div>

                    <div className="d-flex justify-content-end">
                        <Button
                            type="button"
                            mode={ButtonMode.primary}
                            label={translations.saveButton}
                            onClick={() => setShowConfirmPopup(true)}
                        />
                    </div>

                    <PopupConfirm
                        show={showConfirmPopup}
                        onHide={() => setShowConfirmPopup(false)}
                        question={translations.confirmTitle}
                        confirmation={translations.confirm}
                        cancelButtonText={translations.cancelButton}
                        confirmButtonText={translations.confirmButton}
                        onClickConfirm={handleConfirmSubmit}
                        onClickCancel={() => setShowConfirmPopup(false)}
                    />
                </Form>
            </div>
        </MyAccountContainer>
    );
};
